if (!customElements.get('byte-tabbed-product-slider')) {
  class ByteTabbedProductSlider extends HTMLElement {
    constructor() {
      super();

      this.activePanelIndex = 0;
      this.panelStates = [];

      this.boundHandleClick = this.handleClick.bind(this);
      this.boundHandleResize = this.handleResize.bind(this);
      this.boundHandlePointerDown = this.handlePointerDown.bind(this);
      this.boundHandlePointerMove = this.handlePointerMove.bind(this);
      this.boundHandlePointerUp = this.handlePointerUp.bind(this);

      this.resizeObserver = null;
      this.isDragging = false;
      this.dragStartX = 0;
      this.dragStartIndex = 0;
      this.activeViewport = null;
      this.activePanelState = null;
      this._didDrag = false;
    }

    connectedCallback() {
      this.#cacheElements();
      if (this.panelStates.length === 0) return;

      this.#bindEvents();
      this.#selectPanel(this.activePanelIndex, false);

      this.resizeObserver = new ResizeObserver(() => {
        this.#updateActivePanelLayout();
      });

      this.resizeObserver.observe(this);

      for (const panelState of this.panelStates) {
        if (panelState.viewport) {
          this.resizeObserver.observe(panelState.viewport);
        }
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.#updateActivePanelLayout();
        });
      });
    }

    disconnectedCallback() {
      this.#unbindEvents();
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
    }

    handleClick(event) {
      if (this._didDrag) {
        this._didDrag = false;
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const actionTrigger = event.target.closest('[data-tabbed-action]');
      if (actionTrigger && !actionTrigger.hasAttribute('disabled')) {
        const tabbedAction = actionTrigger.dataset.tabbedAction;
        const panelIndex = Number(actionTrigger.dataset.tabbedPanelIndex);

        if (tabbedAction === 'select-tab') {
          event.preventDefault();
          if (!Number.isNaN(panelIndex)) this.#selectPanel(panelIndex, true);
          return;
        }

        if (tabbedAction === 'prev' || tabbedAction === 'next') {
          if (Number.isNaN(panelIndex) || panelIndex !== this.activePanelIndex) return;
          event.preventDefault();
          this.#step(tabbedAction === 'next' ? 1 : -1);
          return;
        }
      }

      const dot = event.target.closest('[data-tabbed-dot]');
      if (dot) {
        event.preventDefault();
        const panelIndex = Number(dot.dataset.tabbedDotPanel);
        const dotIndex = Number(dot.dataset.tabbedDot);
        if (!Number.isNaN(panelIndex) && !Number.isNaN(dotIndex)) {
          this.#goTo(panelIndex, dotIndex);
        }
      }
    }

    handleResize() {
      this.#updateActivePanelLayout();
    }

    handlePointerDown(event) {
      if (event.pointerType === 'mouse') return;

      const viewport = event.target.closest('[data-tabbed-viewport]');
      if (!viewport) return;

      if (this.#isCardInteractiveTarget(event.target)) return;

      const panelState = this.#getPanelStateByViewport(viewport);
      if (!panelState || !panelState.track) return;

      // layout henüz hesaplanmamışsa şimdi hesapla
      if (panelState.visibleCount <= 1 || panelState.slideCount === 0) {
        panelState.visibleCount = this.#getVisibleCount();
        const slideWidth = this.#getSlideWidth(panelState);
        if (slideWidth > 0) {
          const w = `${slideWidth}px`;
          for (const slide of panelState.slides) {
            slide.style.setProperty('--byte-tps-slide-width', w);
            slide.style.flexBasis = w;
            slide.style.flexShrink = '0';
            slide.style.width = w;
            slide.style.maxWidth = w;
          }
        }
      }

      if (!this.#canSlide(panelState)) return;

      this.isDragging = true;
      this._didDrag = false;
      this.activeViewport = viewport;
      this.activePanelState = panelState;
      this.dragStartX = event.clientX;
      this.dragStartIndex = panelState.currentIndex;

      viewport.classList.add('is-dragging');
      panelState.track.classList.remove('is-animating');

      try { viewport.setPointerCapture(event.pointerId); } catch {}
    }

    handlePointerMove(event) {
      if (!this.isDragging || !this.activeViewport || !this.activePanelState) return;

      const panelState = this.activePanelState;
      const slideStride = this.#getSlideStride(panelState);
      if (slideStride <= 0) return;

      const deltaX = this.dragStartX - event.clientX;

      if (!this._didDrag && Math.abs(deltaX) > 5) {
        this._didDrag = true;
      }

      if (!this._didDrag) return;

      const maxIndex = this.#getMaxIndex(panelState);
      let rawOffset = panelState.currentIndex * slideStride + deltaX;

      // rubber-band
      if (rawOffset < 0) {
        rawOffset = rawOffset * 0.25;
      } else if (rawOffset > maxIndex * slideStride) {
        rawOffset = maxIndex * slideStride + (rawOffset - maxIndex * slideStride) * 0.25;
      }

      panelState.track.style.transform = `translate3d(-${rawOffset}px, 0, 0)`;
    }

    handlePointerUp(event) {
      if (!this.isDragging || !this.activeViewport || !this.activePanelState) return;

      const panelState = this.activePanelState;
      const viewport = this.activeViewport;

      this.isDragging = false;
      viewport.classList.remove('is-dragging');

      try { viewport.releasePointerCapture(event.pointerId); } catch {}

      if (!this._didDrag) {
        this.#applyTransform(panelState, false);
        this.activeViewport = null;
        this.activePanelState = null;
        return;
      }

      // snap: mevcut raw offset'ten float index hesapla, en yakına snap et
      const stride = this.#getSlideStride(panelState);
      const matrix = new DOMMatrix(panelState.track.style.transform);
      const currentOffset = -matrix.m41;
      const floatIndex = currentOffset / (stride || 1);
      const maxIndex = this.#getMaxIndex(panelState);

      const deltaX = this.dragStartX - event.clientX;
      let targetIndex;

      if (Math.abs(deltaX) > stride * 0.25) {
        targetIndex = deltaX > 0
          ? Math.ceil(floatIndex)
          : Math.floor(floatIndex);
      } else {
        targetIndex = Math.round(floatIndex);
      }

      targetIndex = Math.min(maxIndex, Math.max(0, targetIndex));
      panelState.currentIndex = targetIndex;

      this.#applyTransform(panelState, true);
      this.#updateNavState(panelState);
      this.#updateDots(panelState);

      this.activeViewport = null;
      this.activePanelState = null;
      // click event pointerup'tan sonra gelir, o yüzden kısa süre bekle
      setTimeout(() => { this._didDrag = false; }, 100);
    }

    #selectPanel(panelIndex, shouldFocusTab) {
      if (panelIndex < 0 || panelIndex >= this.panelStates.length) return;

      this.activePanelIndex = panelIndex;

      for (const [index, panelState] of this.panelStates.entries()) {
        const isActive = index === panelIndex;

        panelState.panel.classList.toggle('is-active', isActive);
        panelState.panel.hidden = !isActive;

        if (panelState.tabButton) {
          panelState.tabButton.setAttribute('aria-selected', String(isActive));
          panelState.tabButton.tabIndex = isActive ? 0 : -1;
        }
      }

      this.#updateViewAllLink();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.#updateActivePanelLayout();
          this.#refreshVisibleThumbnailTracks(panelIndex);
        });
      });

      if (shouldFocusTab && this.panelStates[panelIndex]?.tabButton) {
        this.panelStates[panelIndex].tabButton.focus();
      }
    }

    #step(delta) {
      const panelState = this.panelStates[this.activePanelIndex];
      if (!panelState || !this.#canSlide(panelState)) return;
      this.#goTo(this.activePanelIndex, panelState.currentIndex + delta);
    }

    #goTo(panelIndex, index) {
      const panelState = this.panelStates[panelIndex];
      if (!panelState) return;

      const maxIndex = this.#getMaxIndex(panelState);
      panelState.currentIndex = Math.min(maxIndex, Math.max(0, index));
      this.#applyTransform(panelState, true);
      this.#updateNavState(panelState);
      this.#updateDots(panelState);
    }

    #canSlide(panelState) {
      return panelState.slideCount > panelState.visibleCount;
    }

    #getMaxIndex(panelState) {
      return Math.max(0, panelState.slideCount - panelState.visibleCount);
    }

    #updateViewAllLink() {
      if (!this.viewAllLink) return;

      const activeTab = this.tabButtons[this.activePanelIndex];
      const viewAllUrl = activeTab?.dataset.tabbedViewAllUrl?.trim() || '';
      const viewAllLabel = activeTab?.dataset.tabbedViewAllLabel?.trim() || '';
      const shouldShowViewAll = this.hasAttribute('data-show-view-all') && viewAllUrl !== '';

      this.viewAllLink.classList.toggle('is-hidden', !shouldShowViewAll);

      if (!shouldShowViewAll) {
        this.viewAllLink.setAttribute('href', '#');
        this.viewAllLink.setAttribute('aria-hidden', 'true');
        return;
      }

      this.viewAllLink.href = viewAllUrl;
      this.viewAllLink.removeAttribute('aria-hidden');

      if (viewAllLabel) {
        this.viewAllLink.setAttribute('aria-label', viewAllLabel);
      }
    }

    #updateActivePanelLayout() {
      const panelState = this.panelStates[this.activePanelIndex];
      if (!panelState || !panelState.viewport) return;

      panelState.visibleCount = this.#getVisibleCount();

      const maxIndex = this.#getMaxIndex(panelState);

      if (panelState.currentIndex > maxIndex) {
        panelState.currentIndex = maxIndex;
      }

      const slideWidth = this.#getSlideWidth(panelState);

      if (slideWidth > 0) {
        const slideWidthPx = `${slideWidth}px`;

        for (const slide of panelState.slides) {
          slide.style.setProperty('--byte-tps-slide-width', slideWidthPx);
          slide.style.flexBasis = slideWidthPx;
          slide.style.flexShrink = '0';
          slide.style.width = slideWidthPx;
          slide.style.maxWidth = slideWidthPx;
        }
      }

      this.#rebuildDots(panelState);
      this.#applyTransform(panelState, false);
      this.#updateNavState(panelState);
      this.#updateDots(panelState);
      this.#refreshVisibleThumbnailTracks(this.activePanelIndex);
    }

    #updateNavState(panelState) {
      const maxIndex = this.#getMaxIndex(panelState);
      const isAtStart = panelState.currentIndex <= 0;
      const isAtEnd = panelState.currentIndex >= maxIndex;
      const canSlide = this.#canSlide(panelState);

      if (panelState.prevButton) {
        const isPrevDisabled = !canSlide || isAtStart;
        panelState.prevButton.disabled = isPrevDisabled;
        panelState.prevButton.classList.toggle('is-disabled', isPrevDisabled);
        panelState.prevButton.setAttribute('aria-disabled', String(isPrevDisabled));
      }

      if (panelState.nextButton) {
        const isNextDisabled = !canSlide || isAtEnd;
        panelState.nextButton.disabled = isNextDisabled;
        panelState.nextButton.classList.toggle('is-disabled', isNextDisabled);
        panelState.nextButton.setAttribute('aria-disabled', String(isNextDisabled));
      }
    }

    #applyTransform(panelState, shouldAnimate) {
      const slideStride = this.#getSlideStride(panelState);
      if (slideStride <= 0 || !panelState.track) return;

      const offsetPx = panelState.currentIndex * slideStride;

      if (shouldAnimate) {
        panelState.track.classList.add('is-animating');
      } else {
        panelState.track.classList.remove('is-animating');
      }

      panelState.track.style.transform = `translate3d(-${offsetPx}px, 0, 0)`;
    }

    #totalDots(panelState) {
      return Math.max(1, panelState.slideCount - panelState.visibleCount + 1);
    }

    #rebuildDots(panelState) {
      if (!panelState.dotsContainer) return;

      const total = this.#totalDots(panelState);

      if (total <= 1) {
        panelState.dotsContainer.hidden = true;
        panelState.dotsContainer.innerHTML = '';
        return;
      }

      panelState.dotsContainer.hidden = false;
      panelState.dotsContainer.innerHTML = '';

      const panelIndex = this.panelStates.indexOf(panelState);

      for (let i = 0; i < total; i++) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'byte-tabbed-product-slider__dot';
        btn.dataset.tabbedDot = i;
        btn.dataset.tabbedDotPanel = panelIndex;
        btn.setAttribute('aria-label', `${i + 1} / ${total}`);
        panelState.dotsContainer.appendChild(btn);
      }
    }

    #updateDots(panelState) {
      if (!panelState.dotsContainer) return;

      panelState.dotsContainer.querySelectorAll('[data-tabbed-dot]').forEach((dot, i) => {
        const active = i === panelState.currentIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }

    #getVisibleCount() {
      const columnsValue = getComputedStyle(this).getPropertyValue('--byte-tps-columns').trim();
      const parsedColumns = parseInt(columnsValue, 10);
      return Number.isNaN(parsedColumns) || parsedColumns < 1 ? 1 : parsedColumns;
    }

    #getGap(panelState) {
      if (!panelState.track) return 0;

      const gapValue =
        getComputedStyle(panelState.track).columnGap ||
        getComputedStyle(this).getPropertyValue('--byte-tps-gap');
      const parsedGap = parseFloat(gapValue);

      return Number.isNaN(parsedGap) ? 0 : parsedGap;
    }

    #getViewportContentWidth(panelState) {
      if (!panelState.viewport) return 0;

      const viewportStyles = getComputedStyle(panelState.viewport);
      const paddingInline =
        (parseFloat(viewportStyles.paddingLeft) || 0) + (parseFloat(viewportStyles.paddingRight) || 0);

      return panelState.viewport.clientWidth - paddingInline;
    }

    #getSlideWidth(panelState) {
      const viewportContentWidth = this.#getViewportContentWidth(panelState);
      if (viewportContentWidth <= 0) return 0;

      const visibleCount = panelState.visibleCount || this.#getVisibleCount();
      const totalGap = this.#getGap(panelState) * (visibleCount - 1);

      return Math.floor((viewportContentWidth - totalGap) / visibleCount);
    }

    #getSlideStride(panelState) {
      return this.#getSlideWidth(panelState) + this.#getGap(panelState);
    }

    #getPanelStateByViewport(viewport) {
      if (!viewport) return null;
      return this.panelStates.find((ps) => ps.viewport === viewport) || null;
    }

    #refreshVisibleThumbnailTracks(panelIndex) {
      const panelState = this.panelStates[panelIndex];
      if (!panelState?.panel) return;

      for (const thumbnailMedia of panelState.panel.querySelectorAll('byte-product-card-media')) {
        thumbnailMedia.refresh?.();
      }
    }

    #isCardInteractiveTarget(target) {
      return Boolean(
        target.closest(
          'button, input, select, textarea, label, [data-product-card-thumbnail-action], [data-product-card-thumbnail-track], byte-product-card-media'
        )
      );
    }

    #cacheElements() {
      this.tabButtons = [...this.querySelectorAll('[data-tabbed-action="select-tab"]')];
      this.viewAllLink = this.querySelector('[data-tabbed-view-all]');
      this.panels = [...this.querySelectorAll('[data-tabbed-panel]')];

      const initialIndex = Number(this.dataset.initialTabIndex) || 0;
      this.activePanelIndex = Math.min(
        Math.max(0, initialIndex),
        Math.max(0, this.panels.length - 1)
      );

      this.panelStates = this.panels.map((panel, index) => {
        const viewport = panel.querySelector('[data-tabbed-viewport]');
        const track = panel.querySelector('[data-tabbed-track]');
        const slides = track ? [...track.querySelectorAll('[data-tabbed-slide]')] : [];

        return {
          panel,
          viewport,
          track,
          slides,
          slideCount: slides.length,
          tabButton: this.tabButtons[index] || null,
          prevButton: panel.querySelector('[data-tabbed-action="prev"]'),
          nextButton: panel.querySelector('[data-tabbed-action="next"]'),
          dotsContainer: panel.querySelector('[data-tabbed-dots]'),
          currentIndex: 0,
          visibleCount: 1
        };
      });
    }

    #bindEvents() {
      this.addEventListener('click', this.boundHandleClick);
      window.addEventListener('resize', this.boundHandleResize);

      // pointerdown'ı this'e bağla — tüm tab viewport'larını yakalar
      this.addEventListener('pointerdown', this.boundHandlePointerDown);

      // pointermove ve pointerup window'a bağla — drag viewport dışına çıksa da çalışsın
      window.addEventListener('pointermove', this.boundHandlePointerMove);
      window.addEventListener('pointerup', this.boundHandlePointerUp);
      window.addEventListener('pointercancel', this.boundHandlePointerUp);
    }

    #unbindEvents() {
      this.removeEventListener('click', this.boundHandleClick);
      this.removeEventListener('pointerdown', this.boundHandlePointerDown);
      window.removeEventListener('resize', this.boundHandleResize);
      window.removeEventListener('pointermove', this.boundHandlePointerMove);
      window.removeEventListener('pointerup', this.boundHandlePointerUp);
      window.removeEventListener('pointercancel', this.boundHandlePointerUp);
    }
  }

  customElements.define('byte-tabbed-product-slider', ByteTabbedProductSlider);
}